import { useState, useEffect } from 'react';
import type { ButtonStyleType } from '@simplywallst/ui-core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStripePortalQuery, getStripeCheckoutQuery } from './query';
import type { Plan } from '@/components/OurPlans/components/Plans/interface';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import If from '@/components/If';
import { Modal } from '@simplywallst/ui-core';
import { FeatureList, PlanBtn } from './styled';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import { useDispatch } from 'react-redux';
import useUser from '@/hooks/useUser';
import { withAuth } from '@/utilities/queries';

import { differenceInHours } from 'date-fns';
import { useUserAccess } from '@/hooks/useUserAccess';
import BillingSuccessModal from '../BillingSuccessModal'; //phase this out after full migration to Stripe checkout
import BillingErrorModal from '../BillingErrorModal'; //phase this out after full migration to Stripe checkout
import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';
import { useStripePortal } from '@/hooks/useStripePortal';
import { GrandfatherCoupons } from '@/components/OurPlans/components/Plans/constants';
import { useUserPlans } from '@/hooks/useUserPlans';
import { useLocale } from '@/hooks/useLocale';

import { useSnackbar } from '@/features/snackbar';
import { cancelCancellation } from '@simplywallst/services';
import { useUtmExperiment } from '@/hooks/usePlanUtmExperiment';

const GuestRegistrationModalContainer = loadable(
  () =>
    import(/* webpackPrefetch: true */ '@/components/GuestRegistrationModal')
);
enum ActiveModal {
  NONE,
  REGISTRATION,
  DOWNGRADE,
  SUCCESS,
  RENEW,
  ERROR,
}

interface Props {
  btnStyle: string;
  action: string;
  plan: Plan;
  isLoggedIn: boolean;
  couponId: string;
  children: string;
  onClick?: () => void;
  onComplete?: () => void;
}

export const PlanButton = ({
  btnStyle,
  action,
  plan,
  isLoggedIn,
  couponId,
  children,
  onClick,
  onComplete,
}: Props) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(ActiveModal.NONE);
  const closeModal = () => setActiveModal(ActiveModal.NONE);
  const { isUtmExperiment } = useUtmExperiment();
  const { trackEvent } = useTracking();
  const baseClickTracking = {
    plan: plan.type,
    billingPeriod: plan.interval,
    action: 'click',
    subject: 'page',
  };
  const dispatch = useDispatch();
  const { data: userData } = useUser();
  const { name: planName } = useUserPlans();
  const uac = useUserAccess();
  const { planType: userPlanType } = uac.data;
  const isPaidUser =
    userPlanType !== 'free' && userData.billing.status !== 'trialing';

  const stripeCheckoutEnabled =
    isLoggedIn &&
    RUNTIME_ENV !== 'native' &&
    action === 'upgrade' &&
    !isPaidUser;
  const locale = useLocale();
  const {
    status: checkoutStatus,
    fetchStatus: checkoutFetchStatus,
    refetch: fetchCheckout,
  } = useQuery({
    queryKey: [
      'STRIPE_CHECKOUT',
      plan.id,
      couponId,
      plan.type,
      plan.interval,
      locale,
    ],
    queryFn: getStripeCheckoutQuery,
    enabled: false,
    staleTime: 300000,
  });

  //For stripe portal session URL
  const stripePortalEnabled =
    isLoggedIn &&
    RUNTIME_ENV !== 'native' &&
    !stripeCheckoutEnabled &&
    ['upgrade', 'renew', 'downgrade'].includes(action);

  const {
    status,
    fetchStatus,
    refetch: fetchPortal,
  } = useQuery({
    queryKey: [
      'STRIPE_PORTAL',
      plan.type !== 'free' && !userData.billing.canceled ? plan.id : '',
    ],
    queryFn: getStripePortalQuery,
    enabled: false,
    staleTime: 300000,
  });

  const openStripePortal = async () => {
    if (!stripePortalEnabled) return;
    const { data: stripePortal } = await fetchPortal();

    const portalWindow = window.open('', '_self');
    if (portalWindow && stripePortal && typeof stripePortal.url === 'string') {
      window.history.replaceState({}, '', '/plans');
      portalWindow.location.href = stripePortal.url;
    }
  };

  const { addSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async () => {
      const response = await withAuth(cancelCancellation);
      return response;
    },
    onSuccess() {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['USER_PROFILE'],
        });
        queryClient.invalidateQueries({
          queryKey: ['USER_ACCESS'],
        });
      }, 2000);
      addSnackbar({
        message: 'Thanks for resuming your subscription!',
        type: 'positive',
        lifeTime: 3000,
      });
    },
    onError() {
      addSnackbar({
        message: 'Error trying to resume subscription. Contact support',
        type: 'negative',
        lifeTime: 3000,
      });
    },
  });

  const openStripeCheckout = async () => {
    if (!stripeCheckoutEnabled) return;
    const { data: stripeCheckout } = await fetchCheckout();

    const checkoutWindow = window.open('', '_self');
    if (!checkoutWindow || typeof stripeCheckout?.redirect_url !== 'string')
      return;
    window.history.replaceState({}, '', '/plans');
    checkoutWindow.location.href = stripeCheckout.redirect_url;
  };

  useEffect(() => {
    if (activeModal === ActiveModal.REGISTRATION && isLoggedIn) closeModal();
  }, [activeModal, isLoggedIn]);

  const formatInterval = (interval: string): string => {
    return interval === 'year' ? 'Annual' : 'Monthly';
  };
  const planTrialDays =
    userData.billing.status === 'trialing' &&
    userData.billing.trialStart !== null &&
    userData.billing.trialEnd !== null
      ? Math.round(
          differenceInHours(
            userData.billing.trialEnd,
            userData.billing.trialStart
          ) / 24
        )
      : 0;

  const { data: stripePortal } = useStripePortal();
  const stripePortalUrl = stripePortal?.url;
  const stripeUrlSplit = stripePortalUrl?.split('/');
  const stripeUrlEnd =
    stripeUrlSplit && stripeUrlSplit[stripeUrlSplit.length - 1];
  const isLegacyCoupon = GrandfatherCoupons.includes(
    userData.billing.coupon.id
  );

  const downgradeSurvey = useTypeformDrawer('a1N1rZDc', {
    stripeurl: stripeUrlEnd,
    islegacyplan: isLegacyCoupon,
    planid: planName,
    userid: userData.id,
    countryiso: userData.countryISO,
  });

  const handlePlanButtonClick = (action: string): void => {
    switch (action) {
      case 'login':
        setActiveModal(ActiveModal.REGISTRATION);
        trackEvent({
          ...baseClickTracking,
          modifier: 'register',
        });
        trackEvent({
          type: 'track',
          action: 'click',
          modifier: 'plansButton',
          subject: 'guestRegistrationModal',
        });
        break;
      case 'upgrade':
        if (isPaidUser) {
          openStripePortal();
          trackEvent({
            ...baseClickTracking,
            modifier: 'upgrade',
            planTrialDays: planTrialDays,
          });
          break;
        }
        openStripeCheckout();
        trackEvent({
          ...baseClickTracking,
          modifier: 'openCheckout',
        });
        break;
      case 'downgrade':
        if (plan.type === 'free') {
          downgradeSurvey();
        } else {
          setActiveModal(ActiveModal.DOWNGRADE);
        }
        trackEvent({
          ...baseClickTracking,
          modifier: 'downgrade',
        });
        break;
      case 'renew':
        setActiveModal(ActiveModal.RENEW);
        trackEvent({
          ...baseClickTracking,
          modifier: 'renew',
        });
        break;
      case 'cancelcancellation':
        mutate();
        trackEvent({
          ...baseClickTracking,
          modifier: 'cancelCancellation',
        });
        break;
      case 'continue':
        onComplete && onComplete();
        trackEvent({
          ...baseClickTracking,
          modifier: 'continueAsFree',
        });
        break;
    }
    onClick && onClick();
  };

  return (
    <>
      <PlanBtn
        data-cy-id={`${plan.type}-plan-button`}
        display="block"
        styleType={btnStyle as ButtonStyleType}
        disabled={
          action === 'disabled' ||
          [fetchStatus, checkoutFetchStatus].includes('fetching') ||
          (RUNTIME_ENV === 'native' && action !== 'continue')
        }
        isLoading={[fetchStatus, checkoutFetchStatus].includes('fetching')}
        onClick={() => {
          if (
            [fetchStatus, checkoutFetchStatus].includes('idle') ||
            !isLoggedIn
          )
            handlePlanButtonClick(action);
          if ([status, checkoutStatus].includes('error'))
            setActiveModal(ActiveModal.ERROR);
        }}
      >
        {children}
      </PlanBtn>
      <If
        conditions={{
          registration: activeModal === ActiveModal.REGISTRATION,
          error: activeModal === ActiveModal.ERROR,
          downgrade: activeModal === ActiveModal.DOWNGRADE,
          renew: activeModal === ActiveModal.RENEW,
          successMessage: activeModal === ActiveModal.SUCCESS,
        }}
      >
        {{
          registration: (
            <GuestRegistrationModalContainer
              canClose={true}
              initialType={isUtmExperiment ? 'login' : undefined}
              onClose={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'cancelRegistration',
                });
                closeModal();
              }}
              onRegisterSuccess={() => {
                trackEvent({
                  type: 'track',
                  subject: 'planButton',
                  modifier: 'registration',
                  action: 'success',
                });
                if (isUtmExperiment) {
                  closeModal();
                }
              }}
              onLoginSuccess={() => {
                if (isUtmExperiment) {
                  closeModal();
                }
              }}
            />
          ),
          error: (
            <BillingErrorModal
              handleCloseModal={() => {
                closeModal();
                trackEvent({
                  action: 'close',
                  modifier: 'stripeUrlError',
                  plan: plan.type,
                  billingPeriod: plan.interval,
                });
              }}
            />
          ),
          downgrade: (
            <Modal
              title={`Downgrade to ${plan.name}`}
              primaryLabel="Downgrade"
              onSecondaryClick={() => {
                closeModal();
                trackEvent({
                  ...baseClickTracking,
                  modifier: 'downgradeCancel',
                });
              }}
              onPrimaryClick={() => {
                closeModal();
                openStripePortal();
                trackEvent({
                  ...baseClickTracking,
                  modifier: 'downgradeConfirm',
                });
              }}
            >
              <>
                {plan.type === 'free' ? (
                  <>
                    <p>
                      {`This will come into effect at the end of your current
                      subscription. After this date, you will be on the ${plan.name} Plan
                      and limited to:`}
                    </p>
                    <FeatureList>
                      <li>
                        {`${plan.features.companyReportsPerMonth} company reports per month`}
                      </li>
                      <li>
                        {`${plan.features.companiesAnalysedInPortfolio} stocks analyzed in your portfolio`}
                      </li>
                      {!plan.features.exportToPdf && (
                        <li>No access to PDF or Excel exports</li>
                      )}
                      {!plan.features.stockScreener && (
                        <li>No access to Stock Screener</li>
                      )}
                    </FeatureList>
                    <p>
                      If you have more than one portfolio, only your default
                      portfolio will remain active unless you choose to upgrade
                      again.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      {`Your downgrade to ${plan.name} will come into effect immediately and the remainder of your subscription
                      will be credited towards your new plan. Your new plan will be limited to:`}
                    </p>
                    <FeatureList>
                      <li>
                        {`${plan.features.companyReportsPerMonth} company reports per month`}
                      </li>
                      <li>{`${plan.features.companiesAnalysedInPortfolio} stocks analyzed in your portfolio`}</li>
                      {!plan.features.exportToPdf && (
                        <li>No access to PDF or Excel exports</li>
                      )}
                    </FeatureList>
                  </>
                )}
                <p>
                  {`If you have stocks in your portfolios, we will still analyse the first
                     ${plan.features.companiesAnalysedInPortfolio} stocks you added. All others will be disabled
                  unless you choose to upgrade again.`}
                </p>
              </>
            </Modal>
          ),
          renew: (
            <Modal
              title="Renew"
              onPrimaryClick={() => {
                closeModal();
                openStripePortal();
                trackEvent({
                  ...baseClickTracking,
                  modifier: 'RenewConfirm',
                });
              }}
              primaryLabel="Continue"
              onSecondaryClick={() => {
                closeModal();
                trackEvent({
                  ...baseClickTracking,
                  modifier: 'RenewCancel',
                });
              }}
            >
              <>
                <p>
                  {`In order to switch to ${plan.name} ${formatInterval(
                    plan.interval
                  )} you must first renew your current subscription
                    and then switch to the new plan within the billing portal.`}
                </p>
                <p>
                  Note: If you do nothing you will be automatically switched to
                  the Free Plan once your current subscription expires and you
                  will be no longer charged.
                </p>
              </>
            </Modal>
          ),
          successMessage: (
            <BillingSuccessModal
              planName={plan.name}
              planInterval={plan.interval}
              handleCloseModal={() => {
                closeModal();
                onComplete && onComplete();
                dispatch({ type: ROUTE_DASHBOARD });
              }}
            />
          ),
        }}
      </If>
    </>
  );
};
